import React, { useState, useRef } from 'react';
import { useInstalaciones } from '../hooks/useContentful';
import img11 from '../assets/images/img11.jpg';
import img10 from '../assets/images/img10.jpg';
import img9 from '../assets/images/img9.jpg';
import img6 from '../assets/images/img6.jpg';
import img12 from '../assets/images/img12.jpg';

const InstalacionesSection = () => {
  const { instalaciones, loading, error } = useInstalaciones();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const carouselRef = useRef(null);

  // Static fallback data
  const staticSlides = [
    {
      id: 1,
      title: 'Consultorio Moderno',
      description: 'Equipamiento de última generación para diagnósticos precisos',
      image: img11
    },
    {
      id: 2,
      title: 'Sala de Espera Cómoda',
      description: 'Ambiente acogedor y seguro para tu mascota',
      image: img10
    },
    {
      id: 3,
      title: 'Quirófano Especializado',
      description: 'Tecnología quirúrgica avanzada con protocolos de esterilización',
      image: img9
    },
    {
      id: 4,
      title: 'Equipamiento Avanzado',
      description: 'Tecnología de punta para diagnósticos y tratamientos',
      image: img6
    },
    {
      id: 5,
      title: 'Área de Internación',
      description: 'Salas de recuperación con monitoreo continuo 24/7',
      image: img12
    }
  ];

  // Process Contentful data to extract image URL
  const processedInstalaciones = instalaciones.map(item => ({
    id: item.sys?.id || Math.random(),
    title: item.title || 'Instalación',
    description: item.description || '',
    image: item.image?.fields?.file?.url || img11
  }));

  // Use Contentful data if available, otherwise use static data
  const slides = processedInstalaciones.length > 0 ? processedInstalaciones : staticSlides;

  const nextSlide = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsNavigating(false), 300);
  };

  const prevSlide = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsNavigating(false), 300);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    setTouchEnd(e.changedTouches[0].clientX);
    handleSwipe();
  };

  const handleSwipe = () => {
    if (isNavigating) return;
    if (touchStart - touchEnd > 50) {
      // Swipe left (finger moved right) - go to NEXT (right swipe = forward)
      setIsNavigating(true);
      nextSlide();
      setTimeout(() => setIsNavigating(false), 300);
    }
    if (touchStart - touchEnd < -50) {
      // Swipe right (finger moved left) - go to PREVIOUS (left swipe = back)
      setIsNavigating(true);
      prevSlide();
      setTimeout(() => setIsNavigating(false), 300);
    }
  };

  if (loading) {
    return (
      <section className="py-12 md:py-20 bg-white relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="text-center mb-10 md:mb-16">
            <div className="h-8 md:h-10 bg-gradient-to-r from-purple-200 via-purple-300 to-purple-200 rounded-lg w-64 mx-auto mb-4 animate-shimmer-purple"></div>
            <div className="h-5 md:h-6 bg-gradient-to-r from-purple-100 via-purple-200 to-purple-100 rounded-lg w-96 mx-auto animate-shimmer-purple" style={{ animationDelay: '0.3s' }}></div>
          </div>
          {/* Carousel Skeleton */}
          <div className="relative animate-fadeInUp">
            <div className="w-full h-72 sm:h-96 md:h-[500px] lg:h-[550px] bg-gradient-to-br from-purple-200 via-purple-300 to-purple-200 rounded-2xl animate-shimmer-purple shadow-lg"></div>
            {/* Arrow buttons skeleton */}
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full animate-glow"></div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full animate-glow" style={{ animationDelay: '0.3s' }}></div>
          </div>
          {/* Indicators Skeleton */}
          <div className="flex justify-center items-center gap-2 mt-6">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-purple-300 to-purple-400 rounded-full animate-pulse"
                style={{
                  width: index === 0 ? '32px' : '12px',
                  height: '12px',
                  animationDelay: `${index * 0.1}s`
                }}
              ></div>
            ))}
          </div>
          {/* Features Skeleton */}
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 md:p-10 mt-12 md:mt-16 border border-purple-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="text-center">
                  <div className="h-10 bg-gradient-to-r from-purple-300 to-purple-400 rounded-lg w-24 mx-auto mb-2 animate-shimmer-purple" style={{ animationDelay: `${index * 0.15}s` }}></div>
                  <div className="h-5 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-32 mx-auto animate-shimmer-purple" style={{ animationDelay: `${index * 0.15 + 0.2}s` }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    // Fallback to static content
  }

  return (
    <section id="instalaciones" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-200/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>

      {/* Section Separator */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-pink-400 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20 animate-fadeInUp">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fas fa-hospital"></i>
            Nuestras Instalaciones
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
            Nuestras <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Instalaciones</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto px-4 leading-relaxed">
            Contamos con instalaciones modernas y equipamiento de última generación para brindar la mejor atención a tu mascota.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative animate-fadeInUp">
          {/* Carousel */}
          <div
            ref={carouselRef}
            className="relative w-full overflow-hidden rounded-2xl bg-gray-100 shadow-lg"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Slides */}
            <div className="relative w-full h-72 sm:h-96 md:h-[500px] lg:h-[550px]">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute w-full h-full transition-all duration-500 ease-in-out ${
                    index === currentSlide ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover transition-transform duration-500"
                  />
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/30 transition-opacity duration-500"></div>

                  {/* Content overlay */}
                  <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 lg:p-8 transition-transform duration-500">
                    <h3 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-2 md:mb-3 transition-all duration-300">
                      {slide.title}
                    </h3>
                    <p className="text-xs md:text-sm lg:text-base text-gray-100 max-w-2xl transition-all duration-300">
                      {slide.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 bg-purple-600 hover:bg-purple-700 text-white p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Slide anterior"
            >
              <i className="fas fa-chevron-left text-sm md:text-lg"></i>
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 bg-purple-600 hover:bg-purple-700 text-white p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Siguiente slide"
            >
              <i className="fas fa-chevron-right text-sm md:text-lg"></i>
            </button>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center items-center gap-2 mt-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide
                    ? 'bg-purple-600 w-8 h-3'
                    : 'bg-purple-300 hover:bg-purple-400 w-3 h-3'
                }`}
                aria-label={`Ir a slide ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>

        {/* Features Highlight */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 md:p-6 mt-8 md:mt-12 animate-fadeInUp shadow-sm" style={{ animationDelay: '0.2s' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300 group-hover:scale-110">
                <i className="fas fa-medkit text-purple-600 text-lg group-hover:text-white transition-colors duration-300"></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-1">100%</div>
              <p className="text-gray-700 font-medium text-sm md:text-base">Equipado</p>
            </div>
            <div className="text-center md:border-l md:border-r border-purple-300 group">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300 group-hover:scale-110">
                <i className="fas fa-clock text-purple-600 text-lg group-hover:text-white transition-colors duration-300"></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-1">24/7</div>
              <p className="text-gray-700 font-medium text-sm md:text-base">Disponible</p>
            </div>
            <div className="text-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300 group-hover:scale-110">
                <i className="fas fa-shield-alt text-purple-600 text-lg group-hover:text-white transition-colors duration-300"></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-1">Higiénico</div>
              <p className="text-gray-700 font-medium text-sm md:text-base">y Seguro</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstalacionesSection;
